import { getOrCreate } from '../../../util/index.js';
import { VoucherCategory } from '../../vouchers/index.js';
import { monthStart, nextMonthStart } from './dates.js';
import { effectiveRates, settleDerived, unbookedIn } from './derived.js';
import { addRow } from './history.js';
import { accrue, activate, applyWaiver, isActive, ledgerOpen, runsOf, trancheOpen, } from './ledger.js';
import { accrueDaily, book, carryOf } from './rounding.js';
import { isActiveStream, rootCount, rootIndexOf, shadowIndexOf, streamOf, } from './streams.js';
// Fixed interest compounds in one voucher: the compound interest on its
// reference that started accruing first and still does (D30).
const fixedOwner = (caseState, fixed, day) => {
    let owner;
    for (const entry of caseState.interestEntries) {
        const interest = entry.interest;
        if (entry.appended &&
            interest.roots.includes(fixed) &&
            entry.ledger.closed === null &&
            interest.start !== null &&
            interest.start <= day &&
            (interest.end === null || interest.end > day) &&
            (owner === undefined || interest.start < owner.interest.start)) {
            owner = entry;
        }
    }
    return owner;
};
// Open capitalized interest per root, less its derived interest not yet
// booked: what is open of its streams, less what accrued since the last
// capitalization, which sits at the end. A long plan holds hundreds of
// thousands of tranches, so none of this scans them all.
const capitalizedByRoot = (entry) => {
    const interest = entry.interest;
    const ledger = entry.ledger;
    const amounts = new Float64Array(rootCount(interest));
    ledger.openByStream.forEach((open, stream) => {
        amounts[rootIndexOf(interest, stream)] += open;
    });
    for (const stream of interest.unbooked.keys()) {
        if (isActiveStream(interest, stream)) {
            amounts[rootIndexOf(interest, stream)] -= unbookedIn(entry, stream);
        }
    }
    const { tranches } = ledger;
    for (let i = tranches.length - 1; i >= 0 && tranches[i].from >= ledger.capitalizedBefore; i--) {
        if (isActive(tranches[i])) {
            amounts[rootIndexOf(interest, tranches[i].stream)] -= trancheOpen(tranches[i]);
        }
    }
    return amounts;
};
// Each root's base on `day` (owed, so negative): the principal and the
// interest capitalized on it, then each fixed interest, from the owner's first
// compounding after its date, and the interest capitalized on it.
const rootBases = (caseState, entry, referenceAmount, day) => {
    const interest = entry.interest;
    if (interest.compoundingType === null) {
        return [referenceAmount];
    }
    const capitalized = capitalizedByRoot(entry);
    return [
        referenceAmount - capitalized[0],
        ...interest.roots.map((fixed, index) => {
            let amount = capitalized[index + 1];
            if (fixed.appended &&
                fixed.day < entry.ledger.capitalizedBefore &&
                fixedOwner(caseState, fixed, day) === entry) {
                amount += ledgerOpen(fixed.ledger);
                if (fixed.compoundedIn.at(-1)?.entry !== entry) {
                    fixed.compoundedIn.push({ entry, from: day });
                }
            }
            return 0 - amount;
        }),
    ];
};
const referenceBalance = (caseState, entry) => {
    const refId = entry.interest.refId;
    if (refId == null) {
        return 0;
    }
    const ref = caseState.byId.get(refId);
    if (ref === undefined) {
        return 0;
    }
    if (!ref.appended) {
        return ref.inputAmount;
    }
    settleDerived(ref);
    return ref.ledger !== null
        ? 0 - ledgerOpen(ref.ledger)
        : ref.inputAmount - ref.distributed;
};
const pendingCapitalized = (entry, stream) => {
    const ledger = entry.ledger;
    let amount = -unbookedIn(entry, stream);
    for (const tranche of ledger.tranches) {
        if (tranche.stream === stream &&
            tranche.pending &&
            tranche.from < ledger.capitalizedBefore) {
            amount += tranche.amount - tranche.writtenOff;
        }
    }
    return amount;
};
const chargeEnd = (piece, index) => piece.charges[index + 1] ?? piece.to;
const chargeInstants = (interest, from, to) => {
    if (!interest.startedMonth) {
        return [];
    }
    const charges = [];
    if (monthStart(from) > interest.chargedMonth) {
        charges.push(from);
    }
    for (let month = nextMonthStart(from); month < to; month = nextMonthStart(month)) {
        charges.push(month);
    }
    if (charges.length > 0) {
        interest.chargedMonth = monthStart(charges[charges.length - 1]);
    }
    return charges;
};
// Covered interest accruing on a closed voucher is judged at the next stop
// (D31).
const recordClosedAccrual = (entry, tranche) => {
    if (entry.ceilingCovered &&
        entry.ledger.closed !== null &&
        isActive(tranche)) {
        for (const run of runsOf(tranche)) {
            entry.caseState.closedAccruals.push({
                entry,
                from: run.from,
                to: run.to,
                perDay: run.perDay,
            });
        }
    }
};
// Whether the interest accrues on a day of [from, to) before `until`.
export const accruesIn = (interest, until, from, to) => interest.start !== null &&
    interest.start < to &&
    (interest.end === null || interest.end > from) &&
    until > interest.start;
export const accrueInterest = (caseState, entry, from, to) => {
    const { interest } = entry;
    const { until } = caseState;
    if (interest.start === null) {
        throw new Error('No interest start date on interest');
    }
    if (!accruesIn(interest, until, from, to)) {
        return;
    }
    const segmentFrom = Math.max(from, interest.start);
    const segmentTo = Math.min(interest.end ?? to, to, until);
    if (segmentFrom >= segmentTo) {
        return;
    }
    const rounding = caseState.policy.interestRounding;
    const referenceAmount = referenceBalance(caseState, entry);
    const bases = rootBases(caseState, entry, referenceAmount, segmentFrom);
    const totalBase = bases.reduce((total, base) => total + base, 0);
    const segment = {
        caseState,
        entry,
        appendHist: caseState.options.appendHist === true,
        rounding,
        roundPerDay: rounding === 'perDay' ? Math.round : (value) => value,
        referenceAmount,
        bases,
        totalBase,
        capitalizedAmount: totalBase - referenceAmount,
    };
    const rates = effectiveRates(interest, segmentFrom, segmentTo);
    for (let rateIndex = 0; rateIndex < rates.length; rateIndex++) {
        const rate = rates[rateIndex];
        const next = rates[rateIndex + 1];
        const pieceFrom = rate.time != null && rate.time >= segmentFrom ? rate.time : segmentFrom;
        const pieceTo = next !== undefined && next.time != null && next.time <= segmentTo
            ? next.time
            : segmentTo;
        if (pieceFrom >= pieceTo) {
            continue;
        }
        const piece = {
            rate,
            from: pieceFrom,
            to: pieceTo,
            charges: chargeInstants(interest, pieceFrom, pieceTo),
        };
        const windowRow = accrueReinstated(segment, piece);
        if (interest.startedMonth) {
            accrueStartedMonth(segment, piece);
        }
        else {
            accrueDays(segment, piece, windowRow);
        }
    }
};
// Reinstated interest of voided periods, accrued as pending (D8). Returns the
// history row of a voided period's own window, which its days show as.
const accrueReinstated = (segment, piece) => {
    const { entry, bases, totalBase, roundPerDay, rounding } = segment;
    const { interest, ledger } = entry;
    const { rate } = piece;
    let windowRow = -1;
    for (const shadow of interest.shadows) {
        if (shadow.applied || piece.from >= shadow.voidedAt) {
            continue;
        }
        const shadowTo = Math.min(piece.to, shadow.voidedAt);
        const ownWindow = rate.voidedPeriodId === shadow.periodId;
        const reinstatedRate = ownWindow ? (rate.baseRate ?? rate.rate) : rate.rate;
        const pendingBases = bases.map((_base, root) => 0 - pendingCapitalized(entry, streamOf(interest, shadow.index, root)));
        const reinstatedCapitalized = pendingBases.reduce((total, base) => total + base, 0);
        const reinstatedTotalBase = totalBase + reinstatedCapitalized;
        const historyFields = {
            interest: reinstatedRate,
            referenceVoucherBalance: segment.referenceAmount,
            capitalizedInterestBalance: interest.compoundingType !== null
                ? segment.capitalizedAmount + reinstatedCapitalized
                : undefined,
            reapplied: true,
        };
        if (interest.startedMonth) {
            piece.charges.forEach((charge, index) => {
                if (charge >= shadowTo) {
                    return;
                }
                const diff = roundPerDay(reinstatedTotalBase * reinstatedRate) -
                    roundPerDay(totalBase * rate.rate);
                const row = segment.appendHist && diff < 0
                    ? addRow(interest, shadow.rowIds, {
                        from: charge,
                        to: Math.min(chargeEnd(piece, index), shadowTo),
                        ...historyFields,
                    })
                    : -1;
                const due = diff < 0 ? book(rounding, carryOf(shadow.carries, 0), diff) : 0;
                if (due > 0) {
                    accrue(ledger, {
                        from: charge,
                        to: charge + 1,
                        perDay: due,
                        expiry: interest.expiry,
                        pending: true,
                        stream: streamOf(interest, shadow.index, 0),
                        row,
                    });
                }
            });
            continue;
        }
        const fullPerDay = roundPerDay((reinstatedTotalBase * reinstatedRate) / interest.daysAYear);
        const diffPerDay = fullPerDay - roundPerDay((totalBase * rate.rate) / interest.daysAYear);
        // Above the base nothing is reinstated, and the days show as charged.
        const shown = !ownWindow
            ? diffPerDay
            : rate.rate <= reinstatedRate
                ? fullPerDay
                : 0;
        const row = segment.appendHist && shown !== 0
            ? addRow(interest, shadow.rowIds, {
                from: piece.from,
                to: shadowTo,
                ...historyFields,
            })
            : -1;
        if (ownWindow && shadow.voidedAt <= segment.caseState.until) {
            windowRow = row;
        }
        for (let root = 0; root < bases.length; root++) {
            const rootDiffPerDay = roundPerDay(((bases[root] + pendingBases[root]) * reinstatedRate) /
                interest.daysAYear) - roundPerDay((bases[root] * rate.rate) / interest.daysAYear);
            if (rootDiffPerDay >= 0) {
                continue;
            }
            const accrual = accrueDaily(rounding, carryOf(shadow.carries, root), piece.from, shadowTo, rootDiffPerDay);
            if (accrual !== null) {
                accrue(ledger, {
                    ...accrual,
                    expiry: interest.expiry,
                    pending: true,
                    stream: streamOf(interest, shadow.index, root),
                    row,
                });
            }
        }
    }
    return windowRow;
};
const accrueStartedMonth = (segment, piece) => {
    const { entry, totalBase, roundPerDay, rounding } = segment;
    const { interest, ledger } = entry;
    const { rate } = piece;
    // The previous month's row runs on until the next charge.
    if (segment.appendHist && interest.lastMainRow !== -1) {
        const last = interest.rows[interest.lastMainRow];
        if (last.to === piece.from) {
            last.to = piece.charges[0] ?? piece.to;
        }
    }
    piece.charges.forEach((charge, index) => {
        const exact = roundPerDay(totalBase * rate.rate);
        const overriddenFrozen = rate.voidedPeriodId != null && exact === 0;
        const row = segment.appendHist && !overriddenFrozen
            ? addRow(interest, null, {
                from: charge,
                to: chargeEnd(piece, index),
                interest: rate.rate,
                referenceVoucherBalance: segment.referenceAmount,
                capitalizedInterestBalance: segment.capitalizedAmount,
            })
            : -1;
        const due = exact < 0 ? book(rounding, carryOf(interest.carries, 0), exact) : 0;
        if (due > 0) {
            recordClosedAccrual(entry, accrue(ledger, {
                from: charge,
                to: charge + 1,
                perDay: due,
                expiry: interest.expiry,
                row,
            }));
        }
    });
};
// Daily interest over a rate piece, per root (see `streamOf`).
const accrueDays = (segment, piece, windowRow) => {
    const { entry, bases, roundPerDay, rounding, caseState } = segment;
    const { interest, ledger } = entry;
    const { rate } = piece;
    const days = piece.to - piece.from;
    if (days % 1 !== 0) {
        throw new Error('Number of interest days cannot be with decimal places');
    }
    // A voided period's days show as reapplied at the base rate, as long as
    // the reinstated interest has not expired. A rate above the base is not
    // taken back, so its days keep the rate charged.
    const hidden = rate.voidedPeriodId != null &&
        rate.rate <= (rate.baseRate ?? rate.rate) &&
        interest.shadows.some((shadow) => shadow.periodId === rate.voidedPeriodId &&
            shadow.voidedAt <= caseState.until);
    const chargedRow = {
        from: piece.from,
        to: piece.to,
        interest: rate.rate,
        referenceVoucherBalance: segment.referenceAmount,
        capitalizedInterestBalance: segment.capitalizedAmount,
    };
    const row = !segment.appendHist
        ? -1
        : !hidden
            ? addRow(interest, null, chargedRow)
            : windowRow === -1
                ? -1
                : addRow(interest, getOrCreate(interest.chargedRowIds, windowRow, () => []), chargedRow);
    for (let root = 0; root < bases.length; root++) {
        const perDay = roundPerDay((bases[root] * rate.rate) / interest.daysAYear);
        if (perDay >= 0) {
            continue;
        }
        const accrual = accrueDaily(rounding, carryOf(interest.carries, root), piece.from, piece.to, perDay);
        if (accrual !== null) {
            recordClosedAccrual(entry, accrue(ledger, {
                ...accrual,
                expiry: interest.expiry,
                stream: streamOf(interest, 0, root),
                row,
            }));
        }
    }
};
export const activateShadows = (caseState, at) => {
    for (const entry of caseState.interestEntries) {
        if (!entry.appended) {
            continue;
        }
        const interest = entry.interest;
        for (const shadow of interest.shadows) {
            if (shadow.applied || shadow.voidedAt > at) {
                continue;
            }
            // What was written off while pending was never debt.
            settleDerived(entry);
            // A correction takes accrual as it accrues (D11): what accrued before
            // the reinstated interest became debt, then the reinstated interest.
            applyWaiver(entry.ledger);
            shadow.applied = true;
            activate(entry.ledger, (tranche) => shadowIndexOf(interest, tranche.stream) === shadow.index);
            interest.visibleRowIds = interest.visibleRowIds.concat(shadow.rowIds);
            applyWaiver(entry.ledger);
        }
    }
};
// Interest vouchers in the order v1 walks its state: sorted relative to the
// last appended voucher, which itself comes last.
export const accrualOrder = (caseState) => {
    const last = caseState.lastAppended;
    const appended = caseState.interestEntries.filter((entry) => entry.appended && entry !== last);
    if (last !== null && last.category === VoucherCategory.INTEREST) {
        appended.sort((a, b) => b.instant - a.instant ||
            a.sequence - b.sequence ||
            a.inputIndex - b.inputIndex);
    }
    if (last !== null && last.kind === 'interest') {
        appended.push(last);
    }
    return appended;
};
//# sourceMappingURL=accrual.js.map